// src/components/coordinateGrid/TransformRenderer.tsx
/**
 * TransformRenderer Component
 * 
 * Renders transformation results and construction aids.
 * Phase 5: Transformations
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../types/redux';
import {
  selectAllTransformations,
} from '../../store/slices/transformationsSlice';
import {
  Transformation,
  Point2D,
  ConstructionAid,
} from '../../types/coordinateGrid';
import {
  applyTransformation,
  getConstructionAids,
} from '../../lib/coordinateGrid/transformations';
import { selectAllShapes } from '../../store/slices/gridShapesSlice';
import { selectAllPoints } from '../../store/slices/plotPointsSlice';

interface TransformRendererProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Viewport transform */
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  /** Dark mode */
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

/**
 * Render construction aid as SVG element
 */
function renderConstructionAid(
  aid: ConstructionAid,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number,
  key: string
): React.ReactNode {
  const strokeDasharray =
    aid.strokeStyle === 'dashed'
      ? '5,5'
      : aid.strokeStyle === 'dotted'
      ? '2,2'
      : undefined;

  switch (aid.type) {
    case 'line': {
      if (!aid.start || !aid.end) return null;
      const startScreen = mathToScreen(aid.start, scaleX, scaleY, offsetX, offsetY);
      const endScreen = mathToScreen(aid.end, scaleX, scaleY, offsetX, offsetY);
      return (
        <line
          key={key}
          x1={startScreen.x}
          y1={startScreen.y}
          x2={endScreen.x}
          y2={endScreen.y}
          stroke={aid.color}
          strokeWidth={aid.strokeWidth}
          strokeDasharray={strokeDasharray}
          opacity={0.6}
        />
      );
    }

    case 'arrow': {
      if (!aid.start || !aid.end) return null;
      const startScreen = mathToScreen(aid.start, scaleX, scaleY, offsetX, offsetY);
      const endScreen = mathToScreen(aid.end, scaleX, scaleY, offsetX, offsetY);
      
      // Calculate arrow head
      const dx = endScreen.x - startScreen.x;
      const dy = endScreen.y - startScreen.y;
      const angle = Math.atan2(dy, dx);
      const arrowLength = 10;
      const arrowAngle = Math.PI / 6; // 30 degrees
      
      const arrow1X = endScreen.x - arrowLength * Math.cos(angle - arrowAngle);
      const arrow1Y = endScreen.y - arrowLength * Math.sin(angle - arrowAngle);
      const arrow2X = endScreen.x - arrowLength * Math.cos(angle + arrowAngle);
      const arrow2Y = endScreen.y - arrowLength * Math.sin(angle + arrowAngle);
      
      return (
        <g key={key}>
          <line
            x1={startScreen.x}
            y1={startScreen.y}
            x2={endScreen.x}
            y2={endScreen.y}
            stroke={aid.color}
            strokeWidth={aid.strokeWidth}
            strokeDasharray={strokeDasharray}
            opacity={0.6}
          />
          <polygon
            points={`${endScreen.x},${endScreen.y} ${arrow1X},${arrow1Y} ${arrow2X},${arrow2Y}`}
            fill={aid.color}
            opacity={0.6}
          />
        </g>
      );
    }

    case 'arc': {
      if (!aid.centre || aid.radius === undefined) return null;
      const centreScreen = mathToScreen(aid.centre, scaleX, scaleY, offsetX, offsetY);
      const radiusScreen = aid.radius * scaleX;
      
      const startAngleRad = (aid.startAngle || 0) * (Math.PI / 180);
      const endAngleRad = (aid.endAngle || 0) * (Math.PI / 180);
      
      const startX = centreScreen.x + radiusScreen * Math.cos(startAngleRad);
      const startY = centreScreen.y - radiusScreen * Math.sin(startAngleRad);
      const endX = centreScreen.x + radiusScreen * Math.cos(endAngleRad);
      const endY = centreScreen.y - radiusScreen * Math.sin(endAngleRad);
      
      const largeArc = Math.abs(endAngleRad - startAngleRad) > Math.PI ? 1 : 0;
      const sweep = endAngleRad > startAngleRad ? 1 : 0;
      
      return (
        <path
          key={key}
          d={`M ${startX} ${startY} A ${radiusScreen} ${radiusScreen} 0 ${largeArc} ${sweep} ${endX} ${endY}`}
          fill="none"
          stroke={aid.color}
          strokeWidth={aid.strokeWidth}
          opacity={0.6}
        />
      );
    }

    case 'marker': {
      if (!aid.start) return null;
      const startScreen = mathToScreen(aid.start, scaleX, scaleY, offsetX, offsetY);
      return (
        <circle
          key={key}
          cx={startScreen.x}
          cy={startScreen.y}
          r={4}
          fill={aid.color}
          opacity={0.6}
        />
      );
    }

    default:
      return null;
  }
}

export const TransformRenderer: React.FC<TransformRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const transformations = useSelector(selectAllTransformations);
  const shapes = useSelector(selectAllShapes);
  const points = useSelector(selectAllPoints);

  // Get vertices from shapes/points by ID
  const getTargetVertices = (targetIds: string[]): Point2D[] => {
    const vertices: Point2D[] = [];
    
    for (const id of targetIds) {
      // Check if it's a shape
      const shape = shapes.find(s => s.id === id);
      if (shape) {
        vertices.push(...shape.vertices);
        continue;
      }
      
      // Check if it's a point
      const point = points.find(p => p.id === id);
      if (point) {
        vertices.push({ x: point.x, y: point.y });
        continue;
      }
    }
    
    return vertices;
  };

  // Render transformations
  const transformElements = useMemo(() => {
    return transformations.map((transformation) => {
      const originalVertices = getTargetVertices(transformation.targetIds);
      
      if (originalVertices.length === 0) {
        return null;
      }

      // Apply transformation
      const transformedVertices = applyTransformation(
        originalVertices,
        transformation.parameters
      );

      // Get construction aids
      const constructionAids = transformation.showConstruction
        ? getConstructionAids(transformation, originalVertices, transformedVertices)
        : [];

      // Render original (dashed if showOriginal)
      const originalElements: React.ReactNode[] = [];
      if (transformation.showOriginal) {
        if (originalVertices.length === 1) {
          // Single point
          const screen = mathToScreen(originalVertices[0], scaleX, scaleY, offsetX, offsetY);
          originalElements.push(
            <circle
              key="original-point"
              cx={screen.x}
              cy={screen.y}
              r={4}
              fill="none"
              stroke="#9CA3AF"
              strokeWidth={2}
              strokeDasharray="3,3"
              opacity={0.5}
            />
          );
        } else if (originalVertices.length > 1) {
          // Shape
          const screenVertices = originalVertices.map(v =>
            mathToScreen(v, scaleX, scaleY, offsetX, offsetY)
          );
          const pathData = `M ${screenVertices[0].x} ${screenVertices[0].y} ${screenVertices
            .slice(1)
            .map(v => `L ${v.x} ${v.y}`)
            .join(' ')} Z`;
          
          originalElements.push(
            <path
              key="original-shape"
              d={pathData}
              fill="none"
              stroke="#9CA3AF"
              strokeWidth={2}
              strokeDasharray="5,5"
              opacity={0.5}
            />
          );
        }
      }

      // Render transformed (solid, different color)
      const transformedElements: React.ReactNode[] = [];
      if (transformedVertices.length === 1) {
        // Single point
        const screen = mathToScreen(transformedVertices[0], scaleX, scaleY, offsetX, offsetY);
        transformedElements.push(
          <circle
            key="transformed-point"
            cx={screen.x}
            cy={screen.y}
            r={5}
            fill={transformation.imageColor}
            stroke={transformation.imageColor}
            strokeWidth={2}
            opacity={0.8}
          />
        );
      } else if (transformedVertices.length > 1) {
        // Shape
        const screenVertices = transformedVertices.map(v =>
          mathToScreen(v, scaleX, scaleY, offsetX, offsetY)
        );
        const pathData = `M ${screenVertices[0].x} ${screenVertices[0].y} ${screenVertices
          .slice(1)
          .map(v => `L ${v.x} ${v.y}`)
          .join(' ')} Z`;
        
        transformedElements.push(
          <path
            key="transformed-shape"
            d={pathData}
            fill={transformation.imageColor}
            fillOpacity={0.2}
            stroke={transformation.imageColor}
            strokeWidth={2}
            opacity={0.8}
          />
        );
      }

      // Render labels (A→A', B→B', etc.)
      const labelElements: React.ReactNode[] = [];
      if (originalVertices.length <= 6) {
        // Only show labels for small numbers of vertices
        for (let i = 0; i < Math.min(originalVertices.length, transformedVertices.length); i++) {
          const originalScreen = mathToScreen(originalVertices[i], scaleX, scaleY, offsetX, offsetY);
          const transformedScreen = mathToScreen(transformedVertices[i], scaleX, scaleY, offsetX, offsetY);
          const label = String.fromCharCode(65 + i); // A, B, C, ...
          
          labelElements.push(
            <text
              key={`label-original-${i}`}
              x={originalScreen.x + 8}
              y={originalScreen.y - 8}
              fill="#9CA3AF"
              fontSize="12"
              fontWeight="600"
              opacity={0.7}
            >
              {label}
            </text>
          );
          labelElements.push(
            <text
              key={`label-transformed-${i}`}
              x={transformedScreen.x + 8}
              y={transformedScreen.y - 8}
              fill={transformation.imageColor}
              fontSize="12"
              fontWeight="600"
            >
              {label}'
            </text>
          );
        }
      }

      // Render construction aids
      const aidElements = constructionAids.map((aid, index) =>
        renderConstructionAid(aid, scaleX, scaleY, offsetX, offsetY, `aid-${transformation.id}-${index}`)
      );

      return (
        <g key={transformation.id} className={`transformation-${transformation.id}`}>
          {originalElements}
          {aidElements}
          {transformedElements}
          {labelElements}
        </g>
      );
    });
  }, [transformations, shapes, points, scaleX, scaleY, offsetX, offsetY]);

  return <g className="transform-layer">{transformElements}</g>;
};
